import React, { ReactNode, useCallback, useContext } from 'react';
import { logError } from 'utils/logger';
import { NetworkStatus, useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { startDealApi } from './index';
import { convertLeadToOptions } from '../lib';
import { StartDealRenderProps } from '../types';
import { CommonDataContext } from '../../../contexts/CommonDataContext';
import { normalizeDealerLocations } from '../lib/normalizeDealerLocations';
import {
  CreateLeadProposalResponse,
  CreateLeadProposalVariables,
  DealerLocationOptionsResponse,
  DealerLocationOptionsVariables,
  LeadOptionsResponse,
  LeadResponse,
  LeadVariables,
  PartnerLinksOptionsResponse,
  PartnerLinksOptionsVariables,
} from './types';
import { GET_LENDER_PROFILES_WITH_OCA_ACCESS } from 'queries/common';
import { Account } from 'types';
import { deskingActions } from 'modules/desking/model';
import { useDispatch } from 'react-redux';

interface Props {
  account: Account;
  children: (startDealRenderProps: StartDealRenderProps) => ReactNode;
}

export const ApiContainer = ({ children, account }: Props) => {
  const dispatch = useDispatch();
  const { isAftermarketPartner } = useContext(CommonDataContext);

  const { data, loading: isOptionsLoading } = useQuery<LeadOptionsResponse>(startDealApi.getAccountLeadOptions);
  const { data: plOptionsResponse, loading: isPartnerLinksOptionsLoading } = useQuery<
    PartnerLinksOptionsResponse,
    PartnerLinksOptionsVariables
  >(startDealApi.getPartnerLinksOptions, {
    skip: !account.dynamicsAccountId,
    variables: { dynamicsId: account.dynamicsAccountId! },
  });

  useQuery(GET_LENDER_PROFILES_WITH_OCA_ACCESS, {
    variables: { accountId: account.id },
    onCompleted(data) {
      dispatch(
        deskingActions.setLenderProfilesWithOca({
          accountName: account.name!,
          lenderProfiles: data?.lenderProfilesWithOcaAccess?.data ?? [],
        })
      );
    },
  });

  const [onFetchDealerLocationsByDynamicId, { data: dealerLocationsResponse, networkStatus }] = useLazyQuery<
    DealerLocationOptionsResponse,
    DealerLocationOptionsVariables
  >(startDealApi.getDealerLocationsOptions, {
    notifyOnNetworkStatusChange: true,
  });

  const [getLead, { loading: isLeadLoading }] = useLazyQuery<LeadResponse, LeadVariables>(startDealApi.getLead);

  const fetchLead = async (id: string) => {
    try {
      const { data } = await getLead({ variables: { leadId: id } });

      return data?.lead ?? null;
    } catch (error) {
      logError(error);
      return null;
    }
  };

  const [onCreateLeadProposal, { loading: isProposalLoading }] = useMutation<
    CreateLeadProposalResponse,
    CreateLeadProposalVariables
  >(startDealApi.createLeadProposal);

  const handleCreateLeadProposal = useCallback(
    async (input: CreateLeadProposalVariables['input']): Promise<CreateLeadProposalResponse['leadProposal']> => {
      const { data } = await onCreateLeadProposal({ variables: { input } });

      if (data) return data?.leadProposal;

      return {
        success: false,
        id: null,
        leadId: null,
        vendorGUID: null,
        errorMessage: null,
        vendorOpportunityId: null,
      };
    },
    [onCreateLeadProposal]
  );

  const handleFetchDealerLocations = async (dynamicDealerId: string) => {
    try {
      const { data } = await onFetchDealerLocationsByDynamicId({
        variables: { dynamicsId: dynamicDealerId },
      });

      return data?.dealerLocationsOptions?.locations ?? [];
    } catch (error) {
      logError(error);
      return [];
    }
  };

  return (
    <>
      {children({
        fetchLead,
        isLeadLoading,
        isOptionsLoading,
        isProposalLoading,
        isAftermarketPartner,
        handleCreateLeadProposal,
        handleFetchDealerLocations,
        isPartnerLinksOptionsLoading,
        options: convertLeadToOptions(data?.leadOptions),
        dealerLocationsOptions: normalizeDealerLocations(dealerLocationsResponse) ?? [],
        isDealerLocationsLoading: [NetworkStatus.loading, NetworkStatus.setVariables].includes(networkStatus),
        partnerLinksOptions: plOptionsResponse?.options?.filter(({ value, label }) => !!value && !!label) ?? [],
      })}
    </>
  );
};
